import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import Login from "@/pages/Login";
import LandingPage from "@/pages/LandingPage";
import BlogPage from "@/pages/BlogPage";
import BlogArticlePage from "@/pages/BlogArticlePage";
import LegalPages from "@/pages/LegalPages";
import AboutOperatorPage from "@/pages/AboutOperatorPage";
import CookieConsent from "@/components/CookieConsent";
import MasterDashboard from "@/pages/MasterDashboard";
import WorkerDashboard from "@/pages/WorkerDashboard";
import SuperAdminDashboard from "@/pages/SuperAdminDashboard";
import ClientPortal from "@/pages/ClientPortal";
import ClientDashboard from "@/pages/ClientDashboard";
import { Toaster } from "@/components/ui/sonner";
import InstallPWA from "@/components/InstallPWA";
import AIChatWidget from "@/components/AIChatWidget";
import VoiceAgent from "@/components/VoiceAgent";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to get user from either storage
function getStoredUser() {
  const fromLocal = localStorage.getItem('user');
  const fromSession = sessionStorage.getItem('user');
  const raw = fromLocal || fromSession;
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch (error) {
    console.error('Failed to parse stored user:', error);
    return null;
  }
}

function ProtectedRoute({ children, requiredRole }) {
  const user = getStoredUser();
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // Check if superadmin is managing a company - allow access to master dashboard
  const isSuperadminManaging = user.isSuperadminManaging;
  if (isSuperadminManaging && requiredRole === 'master') {
    return children;
  }
  
  // Allow multiple roles
  if (requiredRole) {
    const allowedRoles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];
    if (!allowedRoles.includes(user.role)) {
      return <Navigate to="/login" replace />;
    }
  }
  
  return children;
}

// Component to show chat widget only when logged in as admin
function ChatWidgetWrapper() {
  const location = useLocation();
  const [showWidget, setShowWidget] = useState(false);
  
  useEffect(() => {
    const user = getStoredUser();
    setShowWidget(user && user.role === 'master' && location.pathname !== '/login');
  }, [location]);
  
  if (!showWidget) return null;
  return <AIChatWidget />;
}

// Voice Agent wrapper - shows for admin users
function VoiceAgentWrapper() {
  const location = useLocation();
  const [userData, setUserData] = useState(null);

  useEffect(() => {
    const user = getStoredUser();
    if (user && (user.role === 'master' || user.role === 'admin') && location.pathname !== '/login') {
      setUserData(user);
    } else {
      setUserData(null);
    }
  }, [location]);

  if (!userData) return null;
  return <VoiceAgent companyId={userData.company_id} />;
}

function AppContent() {
  return (
    <>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/legal" element={<LegalPages />} />
        <Route path="/despre" element={<AboutOperatorPage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:slug" element={<BlogArticlePage />} />
        <Route path="/client-portal/:token" element={<ClientPortal />} />
        <Route path="/p/:slug" element={<ClientPortal />} />
        <Route 
          path="/client"
          element={
            <ProtectedRoute requiredRole="client">
              <ClientDashboard />
            </ProtectedRoute>
          }
        />
        <Route 
          path="/superadmin" 
          element={
            <ProtectedRoute requiredRole="superadmin">
              <SuperAdminDashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/master" 
          element={
            <ProtectedRoute requiredRole={["master", "admin"]}>
              <MasterDashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/worker" 
          element={
            <ProtectedRoute requiredRole="worker">
              <WorkerDashboard />
            </ProtectedRoute>
          } 
        />
        <Route path="/" element={<LandingPage />} />
      </Routes>
      {/* <VoiceAgentWrapper /> */}
      <ChatWidgetWrapper />
      <CookieConsent />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
      <InstallPWA />
      <Toaster position="top-center" richColors />
    </div>
  );
}

export default App;